import React from 'react';
import { Clock, CheckCircle, XCircle, AlertCircle, Loader2, Ban, Eye } from 'lucide-react';
import { ImportJob } from '@/types/import';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface ImportHistoryProps {
  jobs: ImportJob[];
  onViewErrors?: (jobId: string) => void;
}

export const ImportHistory: React.FC<ImportHistoryProps> = ({
  jobs,
  onViewErrors,
}) => {
  const getStatusIcon = (status: ImportJob['status']) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'FAILED':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'CANCELLED':
        return <Ban className="w-4 h-4 text-gray-600" />;
      case 'PROCESSING':
      case 'VALIDATING':
        return <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (status: ImportJob['status']) => {
    const statusStyles = {
      PENDING: 'bg-gray-100 text-gray-700',
      VALIDATING: 'bg-blue-100 text-blue-700',
      PROCESSING: 'bg-blue-100 text-blue-700',
      COMPLETED: 'bg-green-100 text-green-700',
      FAILED: 'bg-red-100 text-red-700',
      CANCELLED: 'bg-gray-100 text-gray-700',
    };

    return (
      <span className={cn(
        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
        statusStyles[status]
      )}>
        {status.charAt(0) + status.slice(1).toLowerCase()}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (jobs.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Clock className="w-12 h-12 mx-auto text-gray-300 mb-3" />
        <p>No import history yet</p>
        <p className="text-sm">Your past imports will appear here</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium text-gray-900">Recent Imports</h4>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                File
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Results
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {jobs.map((job, index) => (
              <tr key={job.id} className={cn(
                'hover:bg-gray-50',
                index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
              )}>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {formatDate(job.createdAt)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {job.fileName || 'Unknown file'}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(job.status)}
                    {getStatusBadge(job.status)}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm">
                  {['COMPLETED', 'FAILED'].includes(job.status) ? (
                    <div className="flex items-center space-x-3">
                      <span className="text-green-600">{job.successCount} success</span>
                      {job.failedCount > 0 && (
                        <span className="text-red-600">{job.failedCount} failed</span>
                      )}
                      {job.skippedCount > 0 && (
                        <span className="text-yellow-600">{job.skippedCount} skipped</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400">
                      {job.processedRows} / {job.totalRows} rows
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {job.failedCount > 0 && onViewErrors && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewErrors(job.id)}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View Errors
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
